import React, { useState, useMemo, useCallback, useEffect } from "react";
import { ClockCircleOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Divider, Form, Radio, Row, message } from "antd";
import { connect } from "dva";
import {} from "moment";
import { TimeSlot } from "@/typedefs/load";
import { getAvailablePickup, getAvailableDropoff } from "@/utils/dates";
import { LoadTimeSlotMap } from "@/utils/enum";
import { childShipmentExchangeOfferIsValid, parentShipmentExchangeOfferIsValid } from "@/utils/exchangeShipment";
import { getGuidFirst8, getRestrictedDaysOfWeekAsString } from "@/utils/utils";
import ShipmentEtaCalendarFooter from "@/components/ShipmentEtaCalendarFooter";
const EnterETAsInputForm = ({ 
// Redux
configPerm, 
// Parent
shipmentData, etasInputFormRef, incrementCurrentShipmentETAInputIndex, createSingleShipmentPropertyValueUpdater, hideConfirmButton, }) => {
    const { id, pickupETADate, pickupETATimeSlotIndex, dropoffETADate, dropoffETATimeSlotIndex, pickupAddress: { dayOfWeekRestricted: pickupDOWRestrict }, dropOffAddress: { dayOfWeekRestricted: dropoffDOWRestrict }, etasValidated, exchangeEtasValidated, } = shipmentData;
    const updateSelectedPickupETADate = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "pickupETADate"), [id, createSingleShipmentPropertyValueUpdater]);
    const updateSelectedPickupETATimeSlotIndex = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "pickupETATimeSlotIndex"), [id, createSingleShipmentPropertyValueUpdater]);
    const updateSelectedDropoffETADate = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "dropoffETADate"), [id, createSingleShipmentPropertyValueUpdater]);
    const updateSelectedDropoffTimeSlotIndex = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "dropoffETATimeSlotIndex"), [id, createSingleShipmentPropertyValueUpdater]);
    const updateEtasValidated = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "etasValidated"), [id, createSingleShipmentPropertyValueUpdater]);
    const updateExchangeEtasValidated = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "exchangeEtasValidated"), [id, createSingleShipmentPropertyValueUpdater]);
    const didntSelectDropoffDate = useMemo(() => dropoffETADate === undefined || dropoffETADate === null, [dropoffETADate]);
    const sameDaySelected = useMemo(() => pickupETADate?.isSame(dropoffETADate, "day"), [pickupETADate, dropoffETADate]);
    const [currentValidPickupDates, setCurrentValidPickupDates] = useState([]);
    const [currentValidDropoffDates, setCurrentValidDropoffDates] = useState([]);
    // Sync form items with state data from shipment being selected for
    useEffect(() => {
        etasInputFormRef.current?.setFieldsValue({
            pickupETADate,
            pickupETATimeSlotIndex,
            dropoffETADate,
            dropoffETATimeSlotIndex,
        });
    }, [pickupETADate, pickupETATimeSlotIndex, dropoffETADate, dropoffETATimeSlotIndex, etasInputFormRef]);
    // Update the valid dates in the calendars whenever the shipment being selected for changes
    useEffect(() => {
        const fetchValidDates = async () => {
            const validPickupDates = await getAvailablePickup(shipmentData?.id);
            setCurrentValidPickupDates(validPickupDates?.availableDates);
        };
        fetchValidDates();
    }, [shipmentData?.id]);
    // Running own validation function and displaying popup if dates and times are out of order
    // ant design doesn't appear to have form wide validator, only field validators
    const checkDatesAreInOrder = useCallback(() => {
        // check that dropoff is not before pickup
        if (dropoffETADate?.isBefore(pickupETADate, "day")) {
            message.error("Dropoff date cannot be before pickup date.");
            return false;
        }
        // if dates are same, times must be in order
        if (dropoffETADate?.isSame(pickupETADate, "day") && dropoffETATimeSlotIndex < pickupETATimeSlotIndex) {
            message.error("Dropoff time cannot be before pickup time.");
            return false;
        }
        // dates are in order and not on same day, so times are valid, as well
        return true;
    }, [dropoffETADate, pickupETADate, dropoffETATimeSlotIndex, pickupETATimeSlotIndex]);
    const handleClickConfirmETAsButton = useCallback(() => {
        etasInputFormRef.current
            ?.validateFields()
            .then(() => {
            if (!checkDatesAreInOrder())
                return;
            updateEtasValidated(true);
            // Advance to next shipment for eta input if there's no valid exchange shipment to offer
            if (!childShipmentExchangeOfferIsValid(shipmentData, configPerm) &&
                !parentShipmentExchangeOfferIsValid(shipmentData, configPerm) &&
                typeof incrementCurrentShipmentETAInputIndex === "function") {
                incrementCurrentShipmentETAInputIndex();
            }
        })
            .catch((err) => {
            console.error(err);
            updateEtasValidated(false);
        });
    }, [
        etasInputFormRef,
        configPerm,
        shipmentData,
        checkDatesAreInOrder,
        updateEtasValidated,
        incrementCurrentShipmentETAInputIndex,
    ]);
    const updateExchangeSelectedDropoffETADate = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "exchangeDropoffETADate"), [id, createSingleShipmentPropertyValueUpdater]);
    const updateExchangeSelectedDropoffETATimeSlotIndex = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "exchangeDropoffETATimeSlotIndex"), [id, createSingleShipmentPropertyValueUpdater]);
    const etasValidatedCheck = useCallback(() => {
        if (etasValidated) {
            updateEtasValidated(false);
        }
    }, [etasValidated, updateEtasValidated]);
    const exchangeEtasValidatedCheck = useCallback(() => {
        if (exchangeEtasValidated) {
            updateExchangeEtasValidated(false);
            updateExchangeSelectedDropoffETADate(null);
            updateExchangeSelectedDropoffETATimeSlotIndex(null);
        }
    }, [
        exchangeEtasValidated,
        updateExchangeEtasValidated,
        updateExchangeSelectedDropoffETADate,
        updateExchangeSelectedDropoffETATimeSlotIndex,
    ]);
    const handleChangePickupDate = useCallback(async (value) => {
        if (!value)
            return;
        const dropoffETATimeEntered = dropoffETATimeSlotIndex !== undefined && dropoffETATimeSlotIndex !== null;
        const newDateSelectedIsSameDay = value.isSame(dropoffETADate, "day");
        // Auto correct selected dropoff time if the pickup time selected is later
        // than currently selected pickup time when date selected is same day
        if (newDateSelectedIsSameDay && dropoffETATimeEntered) {
            if (dropoffETATimeSlotIndex < pickupETATimeSlotIndex) {
                updateSelectedDropoffTimeSlotIndex(pickupETATimeSlotIndex);
            }
        }
        // Recalc valid dropoff dates when the pickup date is changed
        const newValidDropoffDates = await getAvailableDropoff(id, value);
        setCurrentValidDropoffDates(newValidDropoffDates?.availableDates);
        if (dropoffETADate) {
            // Find the next earliest valid dropoff date on or after the new pickup date
            // to swap the selected dropoff date to
            // NOTE: Assumes that validDropoffDates is sorted in ascending chronological order
            const nextEarliestValidDropoffDate = newValidDropoffDates?.availableDates?.find(validDropoffDate => validDropoffDate.isSameOrAfter(value, "day"));
            updateSelectedDropoffETADate(nextEarliestValidDropoffDate);
        }
        const newSelectedDateIsValid = currentValidPickupDates.some(validDate => validDate.isSame(value, "day"));
        if (!newSelectedDateIsValid) {
            message.warn("Selected date is outside the expected valid range.");
        }
        updateSelectedPickupETADate(value);
    }, [
        id,
        dropoffETATimeSlotIndex,
        dropoffETADate,
        currentValidPickupDates,
        pickupETATimeSlotIndex,
        updateSelectedPickupETADate,
        updateSelectedDropoffTimeSlotIndex,
        updateSelectedDropoffETADate,
    ]);
    const triggerChangePickupDate = useCallback((date, skipValidation) => {
        if (!skipValidation) {
            etasValidatedCheck();
            exchangeEtasValidatedCheck();
        }
        handleChangePickupDate(date);
    }, [etasValidatedCheck, exchangeEtasValidatedCheck, handleChangePickupDate]);
    const handleChangeDropoffDate = useCallback((value) => {
        if (!value)
            return;
        etasValidatedCheck();
        exchangeEtasValidatedCheck();
        const pickupETATimeEntered = pickupETATimeSlotIndex !== undefined && pickupETATimeSlotIndex !== null;
        const newDateSelectedIsSameDay = value.isSame(pickupETADate, "day");
        // Auto correct selected dropoff time if the dropoff time selected is later
        // than currently selected pickup time when date selected is same day
        if (newDateSelectedIsSameDay && pickupETATimeEntered) {
            if (dropoffETATimeSlotIndex < pickupETATimeSlotIndex) {
                updateSelectedDropoffTimeSlotIndex(pickupETATimeSlotIndex);
            }
        }
        const newSelectedDateIsValid = currentValidDropoffDates.some(validDate => validDate.isSame(value, "day"));
        if (!newSelectedDateIsValid) {
            message.warn("Selected date is outside the expected valid range.");
        }
        updateSelectedDropoffETADate(value);
    }, [
        pickupETATimeSlotIndex,
        pickupETADate,
        currentValidDropoffDates,
        dropoffETATimeSlotIndex,
        etasValidatedCheck,
        exchangeEtasValidatedCheck,
        updateSelectedDropoffETADate,
        updateSelectedDropoffTimeSlotIndex,
    ]);
    const handleChangePickupTimeSlotIndex = useCallback((value) => {
        etasValidatedCheck();
        exchangeEtasValidatedCheck();
        if (!sameDaySelected) {
            updateSelectedPickupETATimeSlotIndex(value);
            return;
        }
        const dropoffETATimeEntered = dropoffETATimeSlotIndex !== undefined && dropoffETATimeSlotIndex !== null;
        if (dropoffETATimeEntered) {
            if (dropoffETATimeSlotIndex < value) {
                updateSelectedDropoffTimeSlotIndex(value);
            }
        }
        updateSelectedPickupETATimeSlotIndex(value);
    }, [
        sameDaySelected,
        dropoffETATimeSlotIndex,
        etasValidatedCheck,
        exchangeEtasValidatedCheck,
        updateSelectedPickupETATimeSlotIndex,
        updateSelectedDropoffTimeSlotIndex,
    ]);
    const handleChangeDropoffTimeSlotIndex = useCallback((value) => {
        etasValidatedCheck();
        exchangeEtasValidatedCheck();
        updateSelectedDropoffTimeSlotIndex(value);
    }, [etasValidatedCheck, exchangeEtasValidatedCheck, updateSelectedDropoffTimeSlotIndex]);
    const getPickupDisabledDates = useCallback((current) => {
        if (configPerm["CoreService.AdminShipments"]) {
            return current?.isBefore(currentValidPickupDates?.[0], "day");
        }
        return !currentValidPickupDates?.some(validDate => validDate.isSame(current, "day"));
    }, [configPerm, currentValidPickupDates]);
    const getDropoffDisabledDates = useCallback((current) => {
        if (configPerm["CoreService.AdminShipments"]) {
            return current?.isBefore(currentValidDropoffDates?.[0], "day");
        }
        return !currentValidDropoffDates?.some(validDate => validDate.isSame(current, "day"));
    }, [configPerm, currentValidDropoffDates]);
    const renderPickupCalendarFooter = useCallback(() => {
        const restrictedDates = pickupDOWRestrict?.split(",");
        // Don't render footer if no restrictions set on shipment
        if (!pickupDOWRestrict || pickupDOWRestrict.length === 0)
            return null;
        const restrictedDatesString = getRestrictedDaysOfWeekAsString(restrictedDates);
        return React.createElement(ShipmentEtaCalendarFooter, { type: "pickup", restrictedDatesString: restrictedDatesString });
    }, [pickupDOWRestrict]);
    const renderDropoffCalendarFooter = useCallback(() => {
        const restrictedDates = dropoffDOWRestrict?.split(",");
        // Don't render footer if no restrictions set on shipment
        if (!dropoffDOWRestrict || dropoffDOWRestrict.length === 0)
            return null;
        const restrictedDatesString = getRestrictedDaysOfWeekAsString(restrictedDates);
        return React.createElement(ShipmentEtaCalendarFooter, { type: "dropoff", restrictedDatesString: restrictedDatesString });
    }, [dropoffDOWRestrict]);
    // Colors dates that are considered "valid" green to make them easily noticeable on the calendar
    const renderValidPickupDates = useCallback((current) => {
        const style = {};
        // If not autosled admin, display normally
        if (configPerm["CoreService.AdminShipments"]) {
            // If date is part of current valid list, color it green
            if (currentValidPickupDates?.some(validPickupDate => validPickupDate.isSame(current, "day"))) {
                style.backgroundColor = "#d6f3d1";
            }
            // Color the currently selected date the same blue as the calendar normally does
            if (pickupETADate?.isSame(current, "day")) {
                style.backgroundColor = "#2d6ac4";
            }
        }
        return (React.createElement("div", { className: "ant-picker-cell-inner", style: style }, current.date()));
    }, [configPerm, currentValidPickupDates, pickupETADate]);
    const renderValidDropoffDates = useCallback((current) => {
        const style = {};
        if (configPerm["CoreService.AdminShipments"]) {
            if (currentValidDropoffDates?.some(validDropoffDate => validDropoffDate.isSame(current, "day"))) {
                style.backgroundColor = "#d6f3d1";
            }
            if (dropoffETADate?.isSame(current, "day")) {
                style.backgroundColor = "#2d6ac4";
            }
        }
        return (React.createElement("div", { className: "ant-picker-cell-inner", style: style }, current.date()));
    }, [configPerm, currentValidDropoffDates, dropoffETADate]);
    useEffect(() => {
        // Scenario:
        // 1. Fill Review Requirements form
        // 2. Fill Select Company and Driver form
        // 3. Enter Pickup ETA Date
        // 4. Go back to Select Company and Driver
        // 5. Click on next
        // You are now on Enter ETAs form again, the Pickup ETA Date is filled but the Dropoff ETA Date Calendar do not
        // display any green dates because currentValidDropoffDates becomes empty when EnterETAsInputForm is rerendered.
        // This useEffect is a workaround to fix this issue.
        if (pickupETADate && currentValidPickupDates.length > 0 && currentValidDropoffDates.length === 0) {
            triggerChangePickupDate(pickupETADate, true);
        }
    }, [pickupETADate, currentValidPickupDates.length, currentValidDropoffDates.length, triggerChangePickupDate]);
    return (React.createElement(Form, { ref: etasInputFormRef, layout: "vertical", onClick: e => e.stopPropagation() },
        React.createElement(Row, null,
            React.createElement(Col, { flex: 2 },
                React.createElement(Form.Item, { name: "pickupETADate", label: "Pickup ETA Date", rules: [
                        {
                            required: true,
                            message: "Please select a date",
                        },
                    ] },
                    React.createElement(DatePicker, { placeholder: "Select Date", value: pickupETADate, onChange: handleChangePickupDate, defaultPickerValue: currentValidPickupDates?.[0], format: "MM/DD/YYYY", disabledDate: getPickupDisabledDates, renderExtraFooter: renderPickupCalendarFooter, dateRender: renderValidPickupDates, style: { width: "100%" }, allowClear: false })),
                React.createElement(Form.Item, { name: "pickupETATimeSlotIndex", label: "Pickup ETA Time", rules: [{ required: true, message: "Please select a time" }] },
                    React.createElement(Radio.Group, { value: pickupETATimeSlotIndex, onChange: e => handleChangePickupTimeSlotIndex(e.target.value), disabled: !pickupETADate },
                        React.createElement(Radio.Button, { value: TimeSlot.Morning },
                            React.createElement("span", null, LoadTimeSlotMap[TimeSlot.Morning]),
                            React.createElement(ClockCircleOutlined, { style: { marginLeft: 8 } })),
                        React.createElement(Radio.Button, { value: TimeSlot.Afternoon },
                            React.createElement("span", null, LoadTimeSlotMap[TimeSlot.Afternoon]),
                            React.createElement(ClockCircleOutlined, { style: { marginLeft: 8 } })),
                        React.createElement(Radio.Button, { value: TimeSlot.Evening },
                            React.createElement("span", null, LoadTimeSlotMap[TimeSlot.Evening]),
                            React.createElement(ClockCircleOutlined, { style: { marginLeft: 8 } }))))),
            React.createElement(Col, { flex: 1 },
                React.createElement(Divider, { type: "vertical", style: { height: "100%", marginLeft: "50%" } })),
            React.createElement(Col, { flex: 2 },
                React.createElement(Form.Item, { name: "dropoffETADate", label: "Dropoff ETA Date", rules: [
                        {
                            required: true,
                            message: "Please select a date",
                        },
                    ] },
                    React.createElement(DatePicker, { placeholder: "Select Date", style: { width: "100%" }, value: dropoffETADate, onChange: handleChangeDropoffDate, defaultPickerValue: currentValidDropoffDates?.[0], format: "MM/DD/YYYY", disabledDate: getDropoffDisabledDates, renderExtraFooter: renderDropoffCalendarFooter, dateRender: renderValidDropoffDates, allowClear: false, disabled: !pickupETADate })),
                React.createElement(Form.Item, { name: "dropoffETATimeSlotIndex", label: "Dropoff ETA Time", rules: [{ required: true, message: "Please select a time" }] },
                    React.createElement(Radio.Group, { value: dropoffETATimeSlotIndex, onChange: e => handleChangeDropoffTimeSlotIndex(e.target.value) },
                        React.createElement(Radio.Button, { value: TimeSlot.Morning, disabled: didntSelectDropoffDate ||
                                (sameDaySelected && [TimeSlot.Afternoon, TimeSlot.Evening].includes(pickupETATimeSlotIndex)) },
                            React.createElement("span", null, LoadTimeSlotMap[TimeSlot.Morning]),
                            React.createElement(ClockCircleOutlined, { style: { marginLeft: 8 } })),
                        React.createElement(Radio.Button, { value: TimeSlot.Afternoon, disabled: didntSelectDropoffDate || (sameDaySelected && pickupETATimeSlotIndex === TimeSlot.Evening) },
                            React.createElement("span", null, LoadTimeSlotMap[TimeSlot.Afternoon]),
                            React.createElement(ClockCircleOutlined, { style: { marginLeft: 8 } })),
                        React.createElement(Radio.Button, { value: TimeSlot.Evening, disabled: didntSelectDropoffDate },
                            React.createElement("span", null, LoadTimeSlotMap[TimeSlot.Evening]),
                            React.createElement(ClockCircleOutlined, { style: { marginLeft: 8 } })))))),
        !hideConfirmButton && (React.createElement(Button, { block: true, type: "primary", onClick: handleClickConfirmETAsButton, style: { marginTop: 32 } }, `Confirm ETAs for ${getGuidFirst8(id)}`))));
};
EnterETAsInputForm.defaultProps = {
    hideConfirmButton: false,
};
export default connect(({ configs }) => ({
    configPerm: configs.configPerm,
}))(EnterETAsInputForm);
